import { getProfileImage } from "@shared/hooks/useProfile";
import React from "react";
import { Link } from "react-router-dom";
import { UserProfile } from "../types/user";

interface UserProfileDisplayProps {
  profile: Partial<UserProfile> | null | undefined;
  label?: string;
  showAvatar?: boolean;
  avatarSize?: "sm" | "md" | "lg";
  className?: string;
}

/**
 * Component to display user profile information with optional organization
 */
export const UserProfileDisplay: React.FC<UserProfileDisplayProps> = ({
  profile,
  label = "Created by",
  showAvatar = true,
  avatarSize = "sm",
  className = "",
}) => {
  if (!profile) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="text-sm whitespace-nowrap">
          {label}{" "}
          <span className="font-semibold text-gray-800 dark:text-gray-100">
            Unknown
          </span>
        </div>
      </div>
    );
  }

  // Determine avatar size class
  const getAvatarSizeClass = (): string => {
    switch (avatarSize) {
      case "lg":
        return "w-12 h-12";
      case "md":
        return "w-10 h-10";
      case "sm":
      default:
        return "w-8 h-8";
    }
  };

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="flex items-center">
        {/* Avatar */}
        {showAvatar && (
          <img
            src={getProfileImage(profile)}
            alt={profile.name || "User"}
            className={`${getAvatarSizeClass()} rounded-full mr-2 border border-gray-300 dark:border-gray-700`}
          />
        )}
        <div className="text-sm">
          <div className="whitespace-nowrap">
            {label}{" "}
            <Link to={`/community/profile/${profile.id}`}>
              <span className="font-semibold text-gray-800 dark:text-gray-100">
                {profile.name || ""}
              </span>
            </Link>
          </div>

          {/* Organization info if available */}
          {profile.organization && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              from{" "}
              <Link to={`/community/organizations/${profile.organization.id}`}>
                <span className="font-medium">{profile.organization.name}</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
